import { requestPreviewCamera } from '../map/preview-camera';
import type { CameraAttributeValues } from '../map/attribute-camera';
import { requestPreviewRotation } from '../map/preview-rotation';
import { isCuratedStructuresPath } from '../domain/structures-sample';
import type { Ride } from "../domain/ride";
import { isRecordedRideSource } from "../../../renderer/src/ride-source";
import { isRecordingDevice } from "../../../renderer/src/recording-device";
import { calculateRideMetrics } from "../domain/ride";
import { FrameReadiness } from "./frame-readiness";
import type { MapFrameTiming } from '../map/frame-ready';
import { VideoPresentationSchema } from '../../../renderer/src/presentation-contract';
import type { VideoPresentation } from '../../../renderer/src/video-presentation';
import { compositionPresentationUrlWriter } from './composition-presentation-url';
import { DepthOfFieldSchema, DepthOfFieldPreviewSchema } from '../../../renderer/src/depth-of-field-contract';
import type { DepthOfFieldSettings, DepthOfFieldPreview } from '../../../renderer/src/depth-of-field';
import { CinematicSequenceSchema } from '../../../renderer/src/cinematic-sequence-contract';
import type { CinematicSequence } from '../../../renderer/src/cinematic-sequence';
import { cinematicTimeline } from '../domain/cinematic-timeline';
import { LiveCompositionSchema, type LiveComposition } from './live-composition';

export const RENDER_BRIDGE_READY_EVENT = 'overland-traces:render-bridge-ready';

const frameStates = new WeakMap<RideTraceRenderBridge, FrameReadiness>();
const frameTimings = new WeakMap<RideTraceRenderBridge, MapFrameTiming>();
const frameFailures = new WeakMap<RideTraceRenderBridge, string>();

/** Maximum point count accepted across the renderer browser boundary. */
const MAX_RENDER_POINTS = 100_000;

/** Mutable browser surface used only by the isolated video renderer. */
export interface RideTraceRenderBridge {
  readonly ready: boolean;
  readonly progress: number;
  readonly presentationRevision: number;
  readonly lastMapFrameTiming?: MapFrameTiming | undefined;
  loadRide(candidate: unknown): void;
  setProgress(progress: number): void;
  setPresentation(candidate: unknown): void;
  setDepthOfField?(candidate: unknown, preview?: unknown): void;
  setCinematicSequence?(candidate: unknown): void;
  setComposition?(candidate: unknown): void;
  previewCamera?(values: Partial<CameraAttributeValues> | undefined): boolean;
  previewRotation?(rotation: number | undefined): boolean;
  renderFrame(progress: number, timeoutMs: number): Promise<void>;
  dispose(): void;
}

declare global {
  interface Window {
    __rideTraceRender?: RideTraceRenderBridge;
  }
}

/**
 * Installs the narrow browser bridge used for deterministic frame capture.
 *
 * @param setRide - React state dispatcher for validated ride replacement.
 * @param setProgress - React state dispatcher for exact timeline positions.
 * @returns Installed bridge. The caller must remove it during cleanup.
 */
export function installRenderBridge(
  setRide: (ride: Ride) => void,
  setProgress: (progress: number) => void,
  setPresentation?: (presentation: VideoPresentation, revision: number) => void,
  setDepthOfField?: (settings: DepthOfFieldSettings, revision: number, preview: DepthOfFieldPreview) => void,
  setCinematicSequence?: (sequence: CinematicSequence | undefined, revision: number) => void,
  setComposition?: (settings: LiveComposition, revision: number) => void,
): RideTraceRenderBridge {
  const frames = new FrameReadiness();
  const urlWriter = window.location?.search && new URLSearchParams(window.location.search).has('preview')
    ? compositionPresentationUrlWriter() : undefined;
  let revision = 0;
  let loadedRide: Ride | undefined;
  const bridge: RideTraceRenderBridge = {
    get progress() { return frames.progress; },
    get ready() { return frames.ready; },
    get presentationRevision() { return revision; },
    get lastMapFrameTiming() { return frameTimings.get(bridge); },
    loadRide(candidate) {
      const ride = validateInjectedRide(candidate);
      loadedRide = ride;
      frames.reset();
      frameFailures.delete(bridge);
      setRide(ride);
      setProgress(0);
    },
    setProgress(candidate) {
      if (!Number.isFinite(candidate) || candidate < 0 || candidate > 1) {
        throw new RangeError("Render progress must be between zero and one.");
      }
      if (typeof document !== 'undefined') document.documentElement.dataset.renderRequestedProgress = String(candidate);
      if (frames.request(candidate)) setProgress(candidate);
    },
    setComposition(candidate) {
      const settings = LiveCompositionSchema.parse(candidate);
      if (!setComposition) throw new Error('This composition does not support live settings.');
      const progress = Math.max(0, frames.progress);
      frames.reset(); frames.request(progress); frameFailures.delete(bridge);
      revision++;
      setComposition(settings, revision);
    },
    setPresentation(candidate) {
      const presentation = VideoPresentationSchema.parse(candidate);
      if (!setPresentation) throw new Error('This composition does not support presentation updates.');
      const progress = Math.max(0, frames.progress);
      frames.reset();
      frames.request(progress);
      revision++;
      setPresentation(presentation, revision);
      urlWriter?.update(presentation);
    },
    setDepthOfField(candidate, preview = { original: false, mask: false }) {
      const settings = DepthOfFieldSchema.parse(candidate);
      const view = DepthOfFieldPreviewSchema.parse(preview);
      if (!setDepthOfField) throw new Error('This composition does not support depth-of-field updates.');
      const progress = Math.max(0, frames.progress);
      frames.reset(); frames.request(progress); frameFailures.delete(bridge);
      revision++;
      setDepthOfField(settings, revision, view);
    },
    setCinematicSequence(candidate) {
      const sequence = candidate === undefined ? undefined : CinematicSequenceSchema.parse(candidate);
      if (!setCinematicSequence) throw new Error('Reload Studio to use cinematic keyframes with the current renderer.');
      if (sequence) {
        if (!loadedRide) throw new Error('Load a trace before setting cinematic keyframes.');
        cinematicTimeline(loadedRide.points, sequence, 24);
      }
      const progress = Math.max(0, frames.progress);
      frames.reset(); frames.request(progress); frameFailures.delete(bridge);
      revision++;
      setCinematicSequence(sequence, revision);
    },
    previewCamera(values) {
      if (typeof document === 'undefined' || !new URLSearchParams(window.location?.search ?? '').has('preview')) return false;
      return requestPreviewCamera(document, values);
    },
    previewRotation(rotation) {
      if (typeof document === 'undefined' || !new URLSearchParams(window.location?.search ?? '').has('preview')) return false;
      if (rotation !== undefined && !Number.isFinite(rotation)) throw new RangeError('Rotation must be finite.');
      return requestPreviewRotation(document, rotation);
    },
    renderFrame(progress, timeoutMs) {
      bridge.setProgress(progress);
      const failure = frameFailures.get(bridge);
      if (failure) return Promise.reject(new Error(failure));
      return frames.wait(progress, timeoutMs);
    },
    dispose() { bridge.previewCamera?.(undefined); bridge.previewRotation?.(undefined); urlWriter?.dispose(); frames.cancel("Render bridge was disposed."); },
  };
  frameStates.set(bridge, frames);
  window.__rideTraceRender = bridge;
  if (typeof document !== 'undefined') document.dispatchEvent(new Event(RENDER_BRIDGE_READY_EVENT));
  return bridge;
}

export function markRenderFrameError(bridge: RideTraceRenderBridge | null, message: string, revision?: number): void {
  if (!bridge || (revision !== undefined && revision !== bridge.presentationRevision)) return;
  frameFailures.set(bridge, message);
  const frames = frameStates.get(bridge);
  frames?.cancel(message);
  if (frames) frames.ready = false;
}

/**
 * Marks the exact requested map frame as safe to capture.
 *
 * @param bridge - Installed browser render bridge.
 * @param progress - Timeline fraction represented by the rendered map.
 */
export function markRenderFrameReady(
  bridge: RideTraceRenderBridge | null,
  progress: number,
  timing?: MapFrameTiming,
  revision?: number,
): void {
  if (bridge === null) {
    return;
  }
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.renderPaintedProgress = String(progress);
    document.documentElement.dataset.renderPaintedRevision = String(revision);
    document.documentElement.dataset.renderRequestedRevision = String(bridge.presentationRevision);
  }
  if (revision !== undefined && revision !== bridge.presentationRevision) return;
  frameStates.get(bridge)?.complete(progress);
  if (timing) frameTimings.set(bridge, timing);
}

/**
 * Validates untrusted normalized ride data before it reaches React or MapLibre.
 *
 * @param candidate - Value injected by the isolated renderer process.
 * @returns Bounded ride with metrics recomputed from validated points.
 * @throws TypeError when the object shape or text fields are invalid.
 * @throws RangeError when the point count is outside supported limits.
 */
export function validateInjectedRide(candidate: unknown): Ride {
  if (candidate === null || typeof candidate !== "object") {
    throw new TypeError("The render trace must be an object.");
  }
  const input = candidate as Partial<Ride>;
  if (
    typeof input.id !== "string"
    || input.id.length < 1
    || input.id.length > 128
    || typeof input.title !== "string"
    || input.title.length < 1
    || input.title.length > 120
    || typeof input.region !== "string"
    || input.region.length > 160
  ) {
    throw new TypeError("The render trace metadata is invalid.");
  }
  if (!Array.isArray(input.points)) {
    throw new TypeError("The render trace points must be an array.");
  }
  if (input.points.length > MAX_RENDER_POINTS) {
    throw new RangeError(`Render traces may contain at most ${MAX_RENDER_POINTS} points.`);
  }
  const source = isRecordedRideSource(input.source) ? input.source : isCuratedStructuresPath(input) ? "synthetic" : undefined;
  if (!source) throw new TypeError("Open a FIT or GPX trace first.");
  if (input.sourceFileName !== undefined && (typeof input.sourceFileName !== "string" || !input.sourceFileName.length || input.sourceFileName.length > 255)) throw new TypeError("Invalid source filename.");
  const metrics = calculateRideMetrics(input.points, input.timerDurationSeconds);
  if (input.recordingDevice !== undefined && !isRecordingDevice(input.recordingDevice)) {
    throw new TypeError("The recording device metadata is invalid.");
  }
  return {
    id: input.id,
    metrics,
    ...(input.timerDurationSeconds === undefined ? {} : { timerDurationSeconds: input.timerDurationSeconds }),
    points: input.points.map((point) => ({ ...point })),
    region: input.region,
    ...(input.recordingDevice ? { recordingDevice: { ...input.recordingDevice } } : {}),
    source,
    ...(input.sourceFileName ? { sourceFileName: input.sourceFileName } : {}),
    title: input.title,
  };
}
